import { createContext, useContext, useReducer } from 'react';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import union from 'lodash/union';

dayjs.extend(timezone);

// Hooks
import useUser from '@/utils/hooks/swr/useUser';

import { ALBUM_TYPE_AUTOMATIC, ALBUM_TYPE_MANUAL } from '@/lib/albums/albumFunnel/types';

// Lib
import {
    ALBUM_AUTOMATIC_STEP,
    ALBUM_MANUAL_STEP,
    ALBUM_TYPE_STEP
} from '@/lib/albums/albumFunnel/steps';

export const NEXT_STEP = 'NEXT_STEP';
export const PREVIOUS_STEP = 'PREVIOUS_STEP';
export const UPDATE_ALBUM_NAME = 'UPDATE_ALBUM_NAME';
export const ADD_LABEL = 'ADD_LABEL';
export const REMOVE_LABEL = 'REMOVE_LABEL';
export const ADD_HASHTAG = 'ADD_HASHTAG';
export const REMOVE_HASHTAG = 'REMOVE_HASHTAG';
export const UPDATE_POST_TYPES = 'UPDATE_POST_TYPES';
export const UPDATE_PERIOD_SELECTED = 'UPDATE_PERIOD_SELECTED';
export const REMOVE_PERIOD_SELECTED = 'REMOVE_PERIOD_SELECTED';
export const ADD_MEDIA = 'ADD_MEDIA';
export const ADD_MEDIAS = 'ADD_MEDIAS';
export const REMOVE_MEDIA = 'REMOVE_MEDIA';
export const REMOVE_ALL_MEDIA = 'REMOVE_ALL_MEDIA';
export const ADD_ALBUM_TYPE = 'ADD_ALBUM_TYPE';
export const REMOVE_ALBUM_TYPE = 'REMOVE_ALBUM_TYPE';

const AlbumContext = createContext();

const initialData = {
    id: null,
    name: '',
    medias: [],
    type: null,
    currentStep: ALBUM_TYPE_STEP,
    criteria: { hashtags: [], labels: [], post_types: [], started_at: null, ended_at: null }
};

const getNextStep = (type) => {
    switch (type) {
        case ALBUM_TYPE_MANUAL:
            return ALBUM_MANUAL_STEP;
        default:
        case ALBUM_TYPE_AUTOMATIC:
            return ALBUM_AUTOMATIC_STEP;
    }
};

const albumReducer = (state, action) => {
    switch (action.type) {
        case NEXT_STEP:
            return {
                ...state,
                medias: action?.payload === state?.type ? state?.medias : [],
                criteria: action?.payload === state?.type ? state?.criteria : initialData?.criteria,
                type: action?.payload,
                currentStep: getNextStep(action?.payload)
            };
        case PREVIOUS_STEP:
            return {
                ...state,
                currentStep: ALBUM_TYPE_STEP
            };
        case UPDATE_ALBUM_NAME:
            return {
                ...state,
                name: action?.payload
            };
        case ADD_HASHTAG:
            return {
                ...state,
                criteria: {
                    ...state?.criteria,
                    hashtags: [...(state?.criteria?.hashtags ?? []), action?.payload]
                }
            };
        case REMOVE_HASHTAG:
            return {
                ...state,
                criteria: {
                    ...state?.criteria,
                    hashtags: state?.criteria?.hashtags.filter((hashtag) => {
                        return hashtag !== action?.payload;
                    })
                }
            };
        case ADD_LABEL:
            return {
                ...state,
                criteria: {
                    ...state?.criteria,
                    labels: [...(state?.criteria?.labels ?? []), action?.payload]
                }
            };
        case REMOVE_LABEL:
            return {
                ...state,
                criteria: {
                    ...state?.criteria,
                    labels: state?.criteria?.labels.filter((label) => label !== action?.payload)
                }
            };
        case UPDATE_POST_TYPES:
            return {
                ...state,
                criteria: {
                    ...state?.criteria,
                    post_types: action?.payload
                }
            };
        case UPDATE_PERIOD_SELECTED:
            return {
                ...state,
                criteria: {
                    ...state?.criteria,
                    started_at: action?.payload?.[0] ?? null,
                    ended_at: action?.payload?.[1] ?? null
                }
            };
        case REMOVE_PERIOD_SELECTED:
            return {
                ...state,
                criteria: {
                    ...state?.criteria,
                    started_at: null,
                    ended_at: null
                }
            };
        case ADD_MEDIA:
            return {
                ...state,
                medias: [...(state?.medias ?? []), action?.payload]
            };
        case ADD_MEDIAS:
            return {
                ...state,
                medias: union(state?.medias, action?.payload)
            };
        case REMOVE_MEDIA:
            return {
                ...state,
                medias: state?.medias?.filter((mediaId) => mediaId !== action?.payload)
            };
        case REMOVE_ALL_MEDIA:
            return {
                ...state,
                medias: []
            };
        case ADD_ALBUM_TYPE:
            return {
                ...state,
                medias: [],
                criteria: {
                    ...state.criteria,
                    album_type: action.payload,
                    hashtags: [],
                    labels: [],
                    post_types: [],
                    started_at: null,
                    ended_at: null
                }
            };
        case REMOVE_ALBUM_TYPE:
            const newCriteria = {
                ...state.criteria,
                hashtags: [],
                labels: [],
                post_types: [],
                started_at: null,
                ended_at: null
            };
            delete newCriteria.album_type;
            return {
                ...state,
                medias: [],
                criteria: newCriteria
            };
        default: {
            throw new Error(`Unhandled action type: ${action.type}`);
        }
    }
};

const AlbumProvider = ({ children, params }) => {
    let data = initialData;
    const { user } = useUser();
    if (params?.id) {
        const formatDate = (dateToFormat) => {
            if (!dateToFormat) return '';
            return dayjs(dateToFormat).tz(user.settings.timezone).format('YYYY-MM-DDTHH:mm:ss');
        };
        data = {
            ...initialData,
            ...params,
            currentStep: getNextStep(params?.type ?? ALBUM_TYPE_AUTOMATIC),
            medias: params?.medias?.map((media) => media?.id) ?? [],
            criteria: {
                ...initialData?.criteria,
                ...params?.criteria,
                started_at: formatDate(params?.criteria?.started_at),
                ended_at: formatDate(params?.criteria?.ended_at)
            }
        };
    }
    const [albumInfo, dispatch] = useReducer(albumReducer, data);
    return (
        <AlbumContext.Provider
            value={{
                albumInfo,
                dispatch
            }}>
            {children}
        </AlbumContext.Provider>
    );
};

const useAlbumContext = () => {
    const context = useContext(AlbumContext);
    if (!context) {
        throw new Error('useAlbumContext should be used within an AlbumProvider');
    }

    return context;
};

export { AlbumProvider, useAlbumContext };
